import { Box, CircularProgress } from "@mui/material";
import { CoverageTierEnum, RateDesignEnum } from "@platform/openapi-types";
import { type JSX, useMemo } from "react";

import { useGetRateScheduleStats } from "../../../hooks/useGetRateScheduleStats";
import { useIsVolumeBased } from "../../../hooks/useIsVolumeBased";
import { useUpdateRate, useUpdateProposalRate } from "../../../hooks/useRates";
import { isVolumeBasedRate } from "../../../utils/premium-calculations";
import { isCriticalIllnessCoverageType } from "../../../utils/rate-helpers";
import {
  formatAgeBandLabel,
  formatRateValue,
  getEnrolledLabel,
  getTierEnrolledLabel,
  groupRatesByAgeBandGender,
  type AgeBandGenderPair,
} from "../../../utils/rateFormatters";
import { parseIntBaseTen } from "../../../utils/transforms";
import {
  quantityFieldOf,
  quantityFieldOfRate,
  quantityValueOf,
  rateFieldPathBuilder,
} from "../../../utils/rateFieldPath";

import { CompanionRatesSection } from "./CompanionRatesSection";
import { DebouncedNumericInput } from "./NumericInput";
import { RateReviewCell } from "./RateReviewCell";
import {
  RenewalGroupHeaderCell,
  RenewalRowCells,
  RenewalSubHeaderCells,
} from "./RenewalRateCell";
import {
  resolveDestinationPlans,
  useAgeBandedRateCopy,
} from "./useAgeBandedRateCopy";
import {
  AG_BORDER,
  AG_HEADER_BG_COLOR,
  FONT_SIZE,
  gridCellSx,
  gridRowSx,
  headerCellSx,
  inputSx,
  SPACE_BETWEEN,
} from "./constants";

import type { RateCopyColumn } from "./useAgeBandedRateCopy";
import type { ColDef } from "ag-grid-community";
import type { AxiosInstance } from "axios";
import type { FC } from "react";
import type { Rate } from "../../../types";
import type { BenefitPlan, BenefitPlanGroup } from "../../../types/models";

export interface AgeBandedGenderRendererProps {
  plan: BenefitPlan;
  axiosInstance?: AxiosInstance;
  coverageType?: string;
  onRateSaved: () => void;
  readOnly?: boolean;
  benefitPlanGroup?: BenefitPlanGroup;
  /** All plans in the coverage offered as copy destinations. */
  copyDestinationPlans?: BenefitPlan[];
}

export const buildAgeBandedGenderColumns = (
  _group: BenefitPlanGroup,
): ColDef[] => [];

export const buildAgeBandedGenderSummaryColumns = (
  _planId: string,
  _planName: string,
): ColDef[] => [];

// Rate-cell labels, shared by the copy-column config, the copy-button aria labels, and the
// renewal cells so the three stay in sync (and to satisfy no-duplicate-string).
const MALE_EE = "Male EE";
const MALE_SP = "Male SP";
const FEMALE_EE = "Female EE";
const FEMALE_SP = "Female SP";
const MALE = "Male";
const FEMALE = "Female";

// Wide min-widths + horizontal scroll so the dense table stays readable and scrolls within
// the pinned benefit-plan cell rather than squishing (applies to quoted plans too). When
// renewal is shown, `renewalTracks` read-only columns are appended and the wider table
// scrolls within the pinned cell.
//   standard: Age Band | Male Qty/Rate | Female Qty/Rate (+2 renewal)
//   EE/SP:    Age Band | M EE Qty/Rate | M SP Qty/Rate | F EE Qty/Rate | F SP Qty/Rate (+4 renewal)
const gridContainerSx = (
  dataTracks: number,
  showRenewal: boolean,
  renewalTracks: number,
): Record<string, unknown> => ({
  display: "grid",
  gridTemplateColumns: `minmax(90px, 1fr) repeat(${
    showRenewal ? dataTracks + renewalTracks : dataTracks
  }, minmax(76px, 1fr))`,
  width: "100%",
  overflowX: "auto",
});

export const AgeBandedGenderRenderer: FC<AgeBandedGenderRendererProps> = ({
  plan,
  axiosInstance,
  coverageType,
  onRateSaved,
  readOnly,
  benefitPlanGroup,
  copyDestinationPlans,
}) => {
  const {
    rateSchedule,
    companionRateSchedule,
    rates,
    companionRates,
    renewal,
    isLoading,
  } = useGetRateScheduleStats(plan);
  const showRenewal = renewal.showRenewal;

  const hasEmpSpouseSplit = useMemo(
    () =>
      rates.some(
        (rate) =>
          rate.coverageTierCode === CoverageTierEnum.employee ||
          rate.coverageTierCode === CoverageTierEnum.spouseOnly,
      ),
    [rates],
  );

  const ageBandGroups: AgeBandGenderPair[] = useMemo(
    () => groupRatesByAgeBandGender(rates),
    [rates],
  );

  // Mutation hook for updating rates
  const updateRateMutation = useUpdateRate(axiosInstance!);
  const updateProposalRateMutation = useUpdateProposalRate(axiosInstance!);
  const rateMutation =
    plan.rateScheduleSource === "proposal"
      ? updateProposalRateMutation
      : updateRateMutation;

  // Determine if rates use volume-based units (volumePer*, per*OfSalary)
  const isVolumeBased = useIsVolumeBased(rates);
  const quantityFieldName = quantityFieldOf(isVolumeBased);
  const fieldPathOf = rateFieldPathBuilder(rateSchedule);

  const isCriticalIllness = isCriticalIllnessCoverageType(coverageType);

  // Quantity column label adapts to unit type
  const enrolledLabel = getEnrolledLabel(
    isVolumeBased,
    rates[0]?.unit,
    coverageType,
  );

  const handleCommit =
    (parser: (val: string) => number, rateField: keyof Rate) =>
    (rate: Rate, value: string): void => {
      if (!axiosInstance || !rateSchedule?.id || !rate.id) return;
      const numericValue = parser(value);
      if (isNaN(numericValue) || numericValue === rate[rateField]) return;

      rateMutation.mutate(
        {
          scheduleId: rateSchedule.id,
          rateId: rate.id,
          benefitPlanId: plan.id,
          rate: { [rateField]: numericValue },
        },
        { onSuccess: () => onRateSaved() },
      );
    };

  const handleLivesCommit = handleCommit(parseIntBaseTen, "numberOfLives");
  const handleVolumeCommit = handleCommit(parseFloat, "volume");
  const handleQuantityCommit = (rate: Rate, value: string): void =>
    isVolumeBasedRate(rate)
      ? handleVolumeCommit(rate, value)
      : handleLivesCommit(rate, value);

  const handleRateCommit = handleCommit(parseFloat, "rate");

  // Copy columns depend on the layout: standard splits by gender, the EE/SP
  // layout splits by gender and coverage tier.
  const copyColumns: RateCopyColumn[] = useMemo(
    () =>
      hasEmpSpouseSplit
        ? [
            {
              id: "male-ee",
              label: MALE_EE,
              matches: (rate: Rate) =>
                rate.gender === "male" &&
                rate.coverageTierCode === CoverageTierEnum.employee,
            },
            {
              id: "male-sp",
              label: MALE_SP,
              matches: (rate: Rate) =>
                rate.gender === "male" &&
                rate.coverageTierCode === CoverageTierEnum.spouseOnly,
            },
            {
              id: "female-ee",
              label: FEMALE_EE,
              matches: (rate: Rate) =>
                rate.gender === "female" &&
                rate.coverageTierCode === CoverageTierEnum.employee,
            },
            {
              id: "female-sp",
              label: FEMALE_SP,
              matches: (rate: Rate) =>
                rate.gender === "female" &&
                rate.coverageTierCode === CoverageTierEnum.spouseOnly,
            },
          ]
        : [
            {
              id: "male",
              label: MALE,
              matches: (rate: Rate) => rate.gender === "male",
            },
            {
              id: "female",
              label: FEMALE,
              matches: (rate: Rate) => rate.gender === "female",
            },
          ],
    [hasEmpSpouseSplit],
  );

  const destinationPlans = useMemo(
    () => resolveDestinationPlans(plan, copyDestinationPlans, benefitPlanGroup),
    [plan, copyDestinationPlans, benefitPlanGroup],
  );

  const { renderCopyButton, copyDialog } = useAgeBandedRateCopy({
    plan,
    rates,
    rateSchedule,
    columns: copyColumns,
    rateDesign: RateDesignEnum.ageBandedGender,
    destinationPlans,
    updateRateMutation,
    updateProposalRateMutation,
    onRateSaved,
  });

  // The last current-rate column gets a right divider only when renewal columns follow it.
  const currentEndBorderRight = showRenewal ? AG_BORDER : "none";

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 2,
        }}
      >
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (!rateSchedule) {
    return (
      <Box sx={{ p: 1, fontSize: "13px", color: "text.secondary" }}>
        No rate schedule
      </Box>
    );
  }

  const headerBox = (
    <Box
      sx={{
        display: "flex",
        justifyContent: SPACE_BETWEEN,
        alignItems: "center",
        height: "36px",
        backgroundColor: AG_HEADER_BG_COLOR,
        borderBottom: AG_BORDER,
        px: 1,
      }}
    >
      <Box sx={{ fontWeight: 600, fontSize: FONT_SIZE }}>
        Age Banded Gender ({ageBandGroups.length} bands)
      </Box>
    </Box>
  );

  // EE/SP split layout (9 columns)
  if (hasEmpSpouseSplit) {
    const employeeEnrolledLabel = getTierEnrolledLabel(
      rates,
      CoverageTierEnum.employee,
      coverageType,
    );
    const spouseEnrolledLabel = getTierEnrolledLabel(
      rates,
      CoverageTierEnum.spouseOnly,
      coverageType,
    );
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 1,
          overflow: "hidden",
        }}
      >
        {headerBox}
        <Box sx={gridContainerSx(8, showRenewal, 4)}>
          {/* Group header row */}
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }}>Age Band</Box>
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 4",
                justifyContent: "center",
                fontWeight: 600,
              }}
            >
              {MALE}
            </Box>
            <Box
              sx={{
                ...headerCellSx,
                gridColumn: "span 4",
                justifyContent: "center",
                fontWeight: 600,
                borderRight: currentEndBorderRight,
              }}
            >
              {FEMALE}
            </Box>
            <RenewalGroupHeaderCell
              show={showRenewal}
              label={renewal.columnLabel}
              span={4}
            />
          </Box>
          {/* Column labels row */}
          <Box sx={gridRowSx}>
            <Box sx={{ ...headerCellSx }} />
            <Box sx={{ ...headerCellSx }}>EE {employeeEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>EE Rate</span>
              {!readOnly && renderCopyButton("male-ee", MALE_EE)}
            </Box>
            <Box sx={{ ...headerCellSx }}>SP {spouseEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>SP Rate</span>
              {!readOnly && renderCopyButton("male-sp", MALE_SP)}
            </Box>
            <Box sx={{ ...headerCellSx }}>EE {employeeEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>EE Rate</span>
              {!readOnly && renderCopyButton("female-ee", FEMALE_EE)}
            </Box>
            <Box sx={{ ...headerCellSx }}>SP {spouseEnrolledLabel}</Box>
            <Box
              sx={{
                ...headerCellSx,
                borderRight: currentEndBorderRight,
                justifyContent: SPACE_BETWEEN,
                gap: 0.5,
              }}
            >
              <span>SP Rate</span>
              {!readOnly && renderCopyButton("female-sp", FEMALE_SP)}
            </Box>
            <RenewalSubHeaderCells
              show={showRenewal}
              labels={["M EE", "M SP", "F EE", "F SP"]}
            />
          </Box>

          {ageBandGroups.map((group, rowIndex) => {
            const ageBandLabel = formatAgeBandLabel(group.lower, group.upper, {
              isHighestBand: rowIndex === ageBandGroups.length - 1,
              isCriticalIllness,
            });
            const isLastRow =
              rowIndex === ageBandGroups.length - 1 &&
              companionRates.length === 0;
            const baseCellIndex = rowIndex * 8;
            const cellSx = (extraSx?: object): Record<string, unknown> => ({
              ...gridCellSx,
              borderBottom: isLastRow ? "none" : AG_BORDER,
              ...extraSx,
            });

            const renderQty = (
              rate: Rate | undefined,
              label: string,
              cellIdx: number,
            ): JSX.Element => (
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, quantityFieldOfRate(rate))}
                sx={cellSx()}
              >
                {rate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(rate)}
                    onCommit={(value) => handleQuantityCommit(rate, value)}
                    allowDecimals={isVolumeBasedRate(rate)}
                    isCurrency={isVolumeBasedRate(rate)}
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex + cellIdx}
                    columnsInRow={8}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} ${label}` }}
                  />
                )}
              </RateReviewCell>
            );

            const renderRate = (
              rate: Rate | undefined,
              label: string,
              cellIdx: number,
              last = false,
            ): JSX.Element => (
              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(rate, "rate")}
                sx={cellSx(last ? { borderRight: "none" } : undefined)}
              >
                {rate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(rate.rate, rate.unit)}
                    onCommit={(value) => handleRateCommit(rate, value)}
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex + cellIdx}
                    columnsInRow={8}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} ${label}` }}
                  />
                )}
              </RateReviewCell>
            );

            return (
              <Box
                key={`age-band-${group.lower}-${group.upper}`}
                sx={gridRowSx}
              >
                <Box
                  sx={{
                    ...headerCellSx,
                    borderBottom: isLastRow ? "none" : AG_BORDER,
                  }}
                >
                  {ageBandLabel}
                </Box>
                {renderQty(
                  group.maleEeRate,
                  `Male EE ${employeeEnrolledLabel}`,
                  0,
                )}
                {renderRate(group.maleEeRate, "Male EE Rate", 1)}
                {renderQty(
                  group.maleSpRate,
                  `Male SP ${spouseEnrolledLabel}`,
                  2,
                )}
                {renderRate(group.maleSpRate, "Male SP Rate", 3)}
                {renderQty(
                  group.femaleEeRate,
                  `Female EE ${employeeEnrolledLabel}`,
                  4,
                )}
                {renderRate(group.femaleEeRate, "Female EE Rate", 5)}
                {renderQty(
                  group.femaleSpRate,
                  `Female SP ${spouseEnrolledLabel}`,
                  6,
                )}
                {renderRate(
                  group.femaleSpRate,
                  "Female SP Rate",
                  7,
                  !showRenewal,
                )}
                <RenewalRowCells
                  show={showRenewal}
                  renewal={renewal}
                  rowLabelPrefix={ageBandLabel}
                  isLastRow={isLastRow}
                  cells={[
                    { rate: group.maleEeRate, label: MALE_EE },
                    { rate: group.maleSpRate, label: MALE_SP },
                    { rate: group.femaleEeRate, label: FEMALE_EE },
                    { rate: group.femaleSpRate, label: FEMALE_SP },
                  ]}
                />
              </Box>
            );
          })}

          {companionRates.length > 0 && companionRateSchedule && (
            <CompanionRatesSection
              companionRates={companionRates}
              companionRateSchedule={companionRateSchedule}
              planId={plan.id}
              totalColumns={showRenewal ? 13 : 9}
              renewalColumns={Number(showRenewal) * 4}
              onRateSaved={onRateSaved}
            />
          )}
        </Box>
        {copyDialog}
      </Box>
    );
  }

  // Standard layout (5 columns)
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        zIndex: 1,
        overflow: "hidden",
      }}
    >
      {headerBox}
      <Box sx={gridContainerSx(4, showRenewal, 2)}>
        {/* Sub-header Row - Group Labels */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }}>Age Band</Box>
          <Box
            sx={{
              ...headerCellSx,
              gridColumn: "span 2",
              justifyContent: "center",
              fontWeight: 600,
            }}
          >
            {MALE}
          </Box>
          <Box
            sx={{
              ...headerCellSx,
              gridColumn: "span 2",
              justifyContent: "center",
              fontWeight: 600,
              borderRight: currentEndBorderRight,
            }}
          >
            {FEMALE}
          </Box>
          <RenewalGroupHeaderCell
            show={showRenewal}
            label={renewal.columnLabel}
            span={2}
          />
        </Box>
        {/* Column labels row */}
        <Box sx={gridRowSx}>
          <Box sx={{ ...headerCellSx }} />
          <Box sx={{ ...headerCellSx }}>{enrolledLabel}</Box>
          <Box
            sx={{
              ...headerCellSx,
              justifyContent: SPACE_BETWEEN,
              gap: 0.5,
            }}
          >
            <span>Rate</span>
            {!readOnly && renderCopyButton("male", MALE)}
          </Box>
          <Box sx={{ ...headerCellSx }}>{enrolledLabel}</Box>
          <Box
            sx={{
              ...headerCellSx,
              borderRight: currentEndBorderRight,
              justifyContent: SPACE_BETWEEN,
              gap: 0.5,
            }}
          >
            <span>Rate</span>
            {!readOnly && renderCopyButton("female", FEMALE)}
          </Box>
          <RenewalSubHeaderCells show={showRenewal} labels={[MALE, FEMALE]} />
        </Box>

        {ageBandGroups.map((group, rowIndex) => {
          const ageBandLabel = formatAgeBandLabel(group.lower, group.upper, {
            isHighestBand: rowIndex === ageBandGroups.length - 1,
            isCriticalIllness,
          });
          const isLastRow =
            rowIndex === ageBandGroups.length - 1 &&
            companionRates.length === 0;
          const baseCellIndex = rowIndex * 4;

          return (
            <Box key={`age-band-${group.lower}-${group.upper}`} sx={gridRowSx}>
              <Box
                sx={{
                  ...headerCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {ageBandLabel}
              </Box>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.maleRate, quantityFieldName)}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.maleRate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(group.maleRate)}
                    onCommit={(value) =>
                      handleQuantityCommit(group.maleRate!, value)
                    }
                    allowDecimals={isVolumeBased}
                    isCurrency={isVolumeBased}
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Male ${enrolledLabel}`,
                    }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.maleRate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.maleRate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      group.maleRate.rate,
                      rates[0]?.unit,
                    )}
                    onCommit={(value) =>
                      handleRateCommit(group.maleRate!, value)
                    }
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex + 1}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} Male Rate` }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.femaleRate, quantityFieldName)}
                sx={{
                  ...gridCellSx,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.femaleRate && (
                  <DebouncedNumericInput
                    serverValue={quantityValueOf(group.femaleRate)}
                    onCommit={(value) =>
                      handleQuantityCommit(group.femaleRate!, value)
                    }
                    allowDecimals={isVolumeBased}
                    isCurrency={isVolumeBased}
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex + 2}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{
                      "aria-label": `${ageBandLabel} Female ${enrolledLabel}`,
                    }}
                  />
                )}
              </RateReviewCell>

              <RateReviewCell
                planId={plan.id}
                fieldPath={fieldPathOf(group.femaleRate, "rate")}
                sx={{
                  ...gridCellSx,
                  borderRight: currentEndBorderRight,
                  borderBottom: isLastRow ? "none" : AG_BORDER,
                }}
              >
                {group.femaleRate && (
                  <DebouncedNumericInput
                    serverValue={formatRateValue(
                      group.femaleRate.rate,
                      rates[0]?.unit,
                    )}
                    onCommit={(value) =>
                      handleRateCommit(group.femaleRate!, value)
                    }
                    allowDecimals
                    isCurrency
                    sx={inputSx}
                    gridId={`age-banded-gender-${plan.id}`}
                    cellIndex={baseCellIndex + 3}
                    columnsInRow={4}
                    readOnly={readOnly}
                    inputProps={{ "aria-label": `${ageBandLabel} Female Rate` }}
                  />
                )}
              </RateReviewCell>
              <RenewalRowCells
                show={showRenewal}
                renewal={renewal}
                rowLabelPrefix={ageBandLabel}
                isLastRow={isLastRow}
                cells={[
                  { rate: group.maleRate, label: MALE },
                  { rate: group.femaleRate, label: FEMALE },
                ]}
              />
            </Box>
          );
        })}

        {companionRates.length > 0 && companionRateSchedule && (
          <CompanionRatesSection
            companionRates={companionRates}
            companionRateSchedule={companionRateSchedule}
            planId={plan.id}
            isProposalRateSchedule={plan.rateScheduleSource === "proposal"}
            totalColumns={showRenewal ? 7 : 5}
            renewalColumns={Number(showRenewal) * 2}
            onRateSaved={onRateSaved}
          />
        )}
      </Box>
      {copyDialog}
    </Box>
  );
};
